import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";

function PatrolPlan() {
  // State management
  const [selectedArea, setSelectedArea] = useState(() =>
    localStorage.getItem("selectedArea") || "All"
  );
  const [selectedAreas, setSelectedAreas] = useState(() => {
    const stored = localStorage.getItem("selectedAreas");
    return stored ? JSON.parse(stored) : [];
  });
  const [lastSelectedAreas, setLastSelectedAreas] = useState(() => {
    const stored = localStorage.getItem("lastSelectedAreas");
    return stored ? JSON.parse(stored) : "All";
  });
  const [lastLocation, setLastLocation] = useState(() => {
    const stored = localStorage.getItem("wardenLastLocation");
    return stored ? JSON.parse(stored) : { latitude: null, longitude: null };
  });
  const [userLocation, setUserLocation] = useState(() => {
    const stored = localStorage.getItem("wardenLastLocation");
    return stored ? JSON.parse(stored) : { latitude: null, longitude: null };
  });
  const [errorLocation, setErrorLocation] = useState(null);
  const [mapboxEnabled, setMapboxEnabled] = useState(false);
  const [sites, setSites] = useState(() => {
    const stored = localStorage.getItem("cachedSites");
    return stored ? JSON.parse(stored) : [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [boostedSites, setBoostedSites] = useState([]);
  const [routeData, setRouteData] = useState(() => {
    const stored = localStorage.getItem("routeCache");
    return stored ? JSON.parse(stored) : {};
  });
  const [filteredSites, setFilteredSites] = useState([]);
  const [wasMapboxDisabled, setWasMapboxDisabled] = useState(true);
  const [currentPeriod, setCurrentPeriod] = useState("");
  const [dayType, setDayType] = useState("");

  // Function to calculate distance between two points
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c * 1000; // Distance in meters
  }, []);

  // Function to fetch sites with scores
  const fetchSites = async (showLoading = false) => {
    if (showLoading) {
      setLoading(true);
    } else {
      setIsUpdating(true);
    }

    try {
      const response = await axiosInstance.post("/api/v1/sites/scores/", {
        selected_areas: selectedAreas
      });

      if (Array.isArray(response.data)) {
        setSites(response.data);
        // Get the period and day type from the first site (all sites have the same period and day type)
        if (response.data.length > 0) {
          setCurrentPeriod(response.data[0].current_period || "");
          setDayType(response.data[0].day_type || "");
        }
        localStorage.setItem("cachedSites", JSON.stringify(response.data));
        localStorage.setItem("lastSelectedAreas", JSON.stringify(selectedAreas));
        setLoading(false);
      } else {
        console.error("Unexpected response format:", response.data);
        setLoading(false);
        setSites([]);
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      setError("Failed to load sites. Please try again later.");
      setLoading(false);
    } finally {
      if (!showLoading) {
        setIsUpdating(false);
      }
    }
  }

  // Function to fetch boost data
  const fetchBoostData = async () => {
    if (!sites.length) return;

    try {
      const siteIds = sites.map(site => site.site_id);
      const response = await axiosInstance.post("/api/v1/sites/boosts/", {
        site_ids: siteIds,
        current_time: new Date().toISOString()
      });

      if (response.data) {
        setBoostedSites(response.data);
      }
    } catch (error) {
      console.error("Error fetching boost data:", error);
    }
  };

  // Function to fetch route data from Mapbox
  const fetchRouteData = async (sitesNeedingRoutes = null, sitesNeedingRemoval = null) => {
    // Early return if Mapbox is disabled or missing requirements
    if (!mapboxEnabled) {
      console.log("Mapbox is disabled in settings");
      return;
    }

    if (!userLocation?.latitude || !userLocation?.longitude) {
      console.log("Missing user location:", userLocation);
      return;
    }

    if (!sites.length) {
      console.log("No sites available for route calculation");
      return;
    }

    const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    if (!MAPBOX_ACCESS_TOKEN) {
      console.error("Mapbox access token not found in environment variables");
      return;
    }

    console.log("Starting route calculations with:", {
      userLocation,
      siteCount: sites.length,
      mapboxEnabled,
    });

    const startCoords = `${userLocation.longitude},${userLocation.latitude}`;
    const routesData = { ...routeData };
    let count = 0;

    // Remove routes for sites that need to be removed
    if (Array.isArray(sitesNeedingRemoval)) {
      sitesNeedingRemoval.forEach(site => {
        if (routesData.hasOwnProperty(site.site_id)) {
          delete routesData[site.site_id];
          console.log(`Removed route data for site ${site.site_id}`);
        }
      });
    }

    // Exit early if no new routes are needed
    if (!Array.isArray(sitesNeedingRoutes) || sitesNeedingRoutes.length === 0) {
      setRouteData(routesData);
      localStorage.setItem("routeCache", JSON.stringify(routesData));
      return;
    }

    // Process sites in parallel with a concurrency limit
    const concurrencyLimit = 3;
    const chunks = [];
    for (let i = 0; i < sitesNeedingRoutes.length; i += concurrencyLimit) {
      chunks.push(sitesNeedingRoutes.slice(i, i + concurrencyLimit));
    }

    for (const chunk of chunks) {
      await Promise.all(chunk.map(async (site) => {
        // Skip if site coordinates are missing
        if (!site.latitude || !site.longitude) {
          console.warn(`Skipping site ${site.site_id} due to missing coordinates:`, site);
          return;
        }

        try {
          const destinationCoords = `${site.longitude},${site.latitude}`;
          const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${startCoords};${destinationCoords}?geometries=geojson&access_token=${MAPBOX_ACCESS_TOKEN}`;
          count += 1;
          console.log(`Fetching route for site ${site.site_id} from ${startCoords} to ${destinationCoords}`);
          const response = await fetch(url);
          if (!response.ok) {
            console.warn(`Failed to fetch route for site ${site.site_id}: ${response.status}`, await response.text());
            return;
          }

          const data = await response.json();
          if (data.routes?.[0]) {
            const route = data.routes[0];
            const durationSeconds = route.duration;
            const distanceMiles = route.distance * 0.00062137;

            const routeData = {
              duration_seconds: durationSeconds,
              duration_str: `${Math.floor(durationSeconds / 3600)}hr(s) ${Math.floor((durationSeconds % 3600) / 60)}min(s)`,
              distance: distanceMiles,
              distance_str: `${distanceMiles.toFixed(2)} miles`,
              route_score: (1 / (1 + (durationSeconds / 3000))) * 5
            };

            routesData[site.site_id] = routeData;
          }
        } catch (error) {
          console.error(`Error fetching route for site ${site.site_id}:`, error);
        }
      }));
    }

    console.log(`Fetched ${count} routes`);

    // Log the route fetch to the backend
    try {
      await axiosInstance.post('/api/v1/sites/log-route-fetch/', {
        routes_fetched: count
      });
    } catch (err) {
      console.error("Failed to log route fetch:", err);
    }

    setRouteData(routesData);
    localStorage.setItem("routeCache", JSON.stringify(routesData));
  };


  const checkMapboxEnabled = async () => {
    try {
      const response = await axiosInstance.post("/api/v1/appsettings/");
      if (response.data?.appsettings) {
        const mapboxSetting = response.data.appsettings.find(
          setting => setting.setting_name === "mapbox_api"
        );
        const enabled = mapboxSetting ? Boolean(mapboxSetting.setting_value) : false;
        console.log("Mapbox setting:", { enabled, setting: mapboxSetting });
        
        // Check if Mapbox was previously disabled and is now enabled
        if (wasMapboxDisabled && enabled) {
          console.log("Mapbox enabled for the first time, fetching initial routes");
          setMapboxEnabled(enabled);
          setWasMapboxDisabled(false);
          // Wait for state update before fetching routes
          setTimeout(() => {
            if (sites.length > 0 && userLocation?.latitude && userLocation?.longitude) {
              fetchRouteData(sites, []);
            }
          }, 0);
        } else if (enabled !== mapboxEnabled) {
          setMapboxEnabled(enabled);
          setWasMapboxDisabled(!enabled);
        }
        
        // Clear route data if Mapbox is disabled
        if (!enabled) {
          console.log("Mapbox disabled, clearing route data");
          setRouteData({});
          localStorage.removeItem("routeCache");
          setWasMapboxDisabled(true);
        }
      } else {
        console.warn("No appsettings found in response:", response.data);
        setMapboxEnabled(false);
        setWasMapboxDisabled(true);
        // Clear route data if settings not found
        setRouteData({});
        localStorage.removeItem("routeCache");
      }
    } catch (error) {
      console.error("Error checking Mapbox setting:", error);
      setMapboxEnabled(false);
      setWasMapboxDisabled(true);
      // Clear route data on error
      setRouteData({});
      localStorage.removeItem("routeCache");
    }
  };

  const handleGetLastLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;

          if (lastLocation.latitude !== null && lastLocation.longitude !== null) {
            // Calculate distance only if lastLocation is available
            const distance = calculateDistance(
              latitude,
              longitude,
              lastLocation.latitude,
              lastLocation.longitude
            );

            if (distance > 500) {
              // Update state with new location
              setLastLocation({ 'latitude': latitude, 'longitude': longitude });
              setUserLocation({ 'latitude': latitude, 'longitude': longitude });
              localStorage.setItem("wardenLastLocation", JSON.stringify({ 'latitude': latitude, 'longitude': longitude }));
              console.log("Location changed significantly, updating:", { latitude, longitude, distance });
            }
          } else {
            setLastLocation({ 'latitude': latitude, 'longitude': longitude });
            setUserLocation({ 'latitude': latitude, 'longitude': longitude });
            localStorage.setItem("wardenLastLocation", JSON.stringify({ 'latitude': latitude, 'longitude': longitude }));
            console.log("Initial location stored:", { latitude, longitude });
          }
        },
        (error) => {
          setErrorLocation("Error getting geolocation: " + error.message);
          console.error("Geolocation error:", error);
        }
      );
    } else {
      setErrorLocation("Geolocation is not supported by this browser.");
    }
  };

  useEffect(() => {
    handleGetLastLocation();
    const intervalId = setInterval(handleGetLastLocation, 10000);
    return () => clearInterval(intervalId);
  }, []);

  // Effect for boost updates - run every 5 minutes
  useEffect(() => {
    if (!sites.length) return;

    fetchBoostData();
    const interval = setInterval(fetchBoostData, 30000); // 0.5 minutes
    return () => clearInterval(interval);
  }, [sites]); // Only depend on sites array

  // Area change handler
  const handleAreaChange = useCallback((event) => {
    setSelectedArea(event.target.value);
    localStorage.setItem("selectedArea", event.target.value);
  }, []);

  useEffect(() => {
    fetchSites(); // Run immediately on mount
  
    const getDelayToNextMinute = () => {
      const now = new Date();
      return (60 - now.getSeconds()) * 1000;
    };
  
    const shouldRunThisMinute = (minute) => (minute % 15 === 1);
  
    const tick = () => {
      const now = new Date();
      if (shouldRunThisMinute(now.getMinutes())) {
        fetchSites();
      }
  
      setTimeout(tick, getDelayToNextMinute());
    };
  
    const initialTimeout = setTimeout(tick, getDelayToNextMinute());
  
    return () => clearTimeout(initialTimeout);
  }, [selectedAreas]);  

  // Effect for route updates
  useEffect(() => {
    // Early return if requirements not met
    if (!mapboxEnabled) {
      console.log("Mapbox is disabled");
      return;
    }

    if (!lastLocation?.latitude || !lastLocation?.longitude) {
      console.log("Waiting for valid location");
      return;
    }

    if (!sites.length) {
      console.log("Waiting for sites");
      return;
    }

    const selectedAreasChanged = JSON.stringify(lastSelectedAreas) !== JSON.stringify(selectedAreas);
    const lastLocationChanged = lastLocation.latitude !== userLocation.latitude ||
      lastLocation.longitude !== userLocation.longitude;

    console.log("selectedAreasChanged", selectedAreasChanged);
    console.log("lastLocationChanged", lastLocationChanged);
    if (selectedAreasChanged || lastLocationChanged) {
      console.log('Triggering route calculation due to:', {
        selectedAreasChanged,
        lastLocationChanged,
        mapboxEnabled,
        hasLocation: Boolean(lastLocation?.latitude && lastLocation?.longitude),
        siteCount: sites.length
      });

      if (lastLocationChanged) {
        const siteIds = sites.map(site => String(site.site_id));
        const routeSiteIds = Object.keys(routeData);

        // Remove any routeData entries whose site_id is no longer in sites
        const sitesNeedingRemoval = routeSiteIds
          .filter(id => !siteIds.includes(id))
          .map(id => sites.find(site => String(site.site_id) === id) || { site_id: id });
        console.log("routeSiteIds", routeSiteIds);
        console.log("siteIds", siteIds);
        console.log("sitesNeedingRemoval", sitesNeedingRemoval);

        // Recalculate routes for all current sites (location changed)
        fetchRouteData(sites, sitesNeedingRemoval);

      } else if (selectedAreasChanged) {
        const siteIds = sites.map(site => String(site.site_id));
        const routeSiteIds = Object.keys(routeData);

        // Remove routeData entries no longer relevant
        const sitesNeedingRemoval = routeSiteIds
          .filter(id => !siteIds.includes(id))
          .map(id => sites.find(site => String(site.site_id) === id) || { site_id: id });

        // Only fetch new routes for sites not already in routeData
        const sitesNeedingRoutes = sites.filter(
          site => !routeSiteIds.includes(String(site.site_id))
        );

        console.log("sitesNeedingRoutes", sitesNeedingRoutes);
        console.log("sitesNeedingRemoval", sitesNeedingRemoval);
        console.log("routeSiteIds", routeSiteIds);

        fetchRouteData(sitesNeedingRoutes, sitesNeedingRemoval);
      }


    }
  }, [lastLocation, selectedAreas, mapboxEnabled, sites]);

  useEffect(() => {
    if (!sites.length) return;

    const filteredSites = sites
      .filter(site => selectedAreas.includes(site.area_name))
      .filter(site => selectedArea === "All" || site.area_name === selectedArea)
      .map(site => {
        const routeInfo = routeData[site.site_id];
        const boostValue = boostedSites[site.site_id] || 0;

        return {
          ...site,
          route: routeInfo,
          route_score: routeInfo?.route_score || 0,
          boost: boostValue,
          final_score: (site.base_score || 0) +
            (site.weather_score || 0) +
            (site.event_score || 0) +
            boostValue +
            (routeInfo?.route_score || 0)
        };
      })
      .sort((a, b) => b.final_score - a.final_score);

    setFilteredSites(filteredSites);
  }, [sites, selectedArea, selectedAreas, routeData, boostedSites]);

  useEffect(() => {
    checkMapboxEnabled();
  }, [boostedSites]);

  // Show loading state if either loading or no sites are available
  const showLoading = loading;
  console.log("routeData", Object.keys(routeData).length);

  return (
    <div className="p-4 max-w-md mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">PatrolPlan</h1>
        <div className="flex items-center space-x-2 mt-1">
        {isUpdating && (
            <div className="text-sm text-gray-500">
            Updating data in background...
          </div>
        )}
          {errorLocation && (
            <div className="text-sm text-red-600">
              {errorLocation}
            </div>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label
          htmlFor="area-dropdown"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Filter by Area:
        </label>
        <select
          id="area-dropdown"
          value={selectedArea}
          onChange={handleAreaChange}
          className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-customIndigo"
        >
          <option value="All">All Selected Areas</option>
          {selectedAreas.length > 0 ? (
            selectedAreas.map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))
          ) : (
            <option disabled>No areas available</option>
          )}
        </select>
      </div>

      {/* Sites Table */}
      <div className="w-full overflow-x-auto rounded-lg shadow">
        {selectedAreas.length === 0 ? (
          <div className="p-4 text-center text-gray-700 bg-gray-50">
            <p className="text-sm">No areas selected. Please go to Locations to select areas.</p>
            <Link 
              to="/choose-locations" 
              className="mt-2 inline-block text-sm text-customIndigo hover:underline"
            >
              Go to Choose Locations
            </Link>
          </div>
        ) : showLoading ? (
          <div className="p-8 flex flex-col items-center justify-center bg-gray-50">
            <div className="w-8 h-8 border-4 border-customIndigo border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-sm text-gray-700">Loading sites...</p>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 bg-gray-50">
            {error}
          </div>
        ) : filteredSites.length === 0 ? (
          <div className="p-4 text-center text-gray-700 bg-gray-50">
            <p className="text-sm">No sites available for the selected area.</p>
          </div>
        ) : (
          <div className="min-w-full divide-y divide-gray-100">
            <div className="bg-gray-50/50">
              <div className="px-2 py-1.5 text-xs font-medium text-gray-500 uppercase">
                {currentPeriod && dayType && (
                  <div className="text-center mb-1">
                    <span className="text-sm font-semibold text-customIndigo">
                      {dayType.charAt(0).toUpperCase() + dayType.slice(1)}: {currentPeriod}
                    </span>
                  </div>
                )}
                <div className="flex">
                  <div className="flex-1">Site Name</div>
                  <div className="w-24 text-right">Score</div>
                </div>
              </div>
            </div>
            <div className="bg-white divide-y divide-gray-100">
              {filteredSites.map((site) => {
                const baseLightness = 0.45;
                const maxLightness = 0.7;
                const gradientStep = (maxLightness - baseLightness) / Math.max(filteredSites.length - 1, 1);
                const currentLightness = baseLightness + filteredSites.indexOf(site) * gradientStep;

                const rowBgColor = site.boost > 0
                  ? '#4eb300' // Bright green for boosted sites
                  : `oklch(${currentLightness.toFixed(3)} 0.1041 243.43)`; // Original blue hue

                return (
                  <div
                    key={`${site.site_id}-${site.site_name}`}
                    style={{ backgroundColor: rowBgColor }}
                    className="flex px-2 py-1.5 hover:brightness-110 transition"
                  >
                    <div className="flex-1 text-white">
                      <Link
                        to={`/patrolplan/${site.site_id}/${encodeURIComponent(site.site_name)}`}
                        state={{
                          scoreBreakdown: {
                            base_score: site.base_score,
                            weather_score: site.weather_score,
                            boost_score: site.boost,
                            event_score: site.event_score,
                            route_score: site.route_score,
                            final_score: site.final_score,
                            current_period: site.current_period,
                            day_type: site.day_type
                          },
                          is_boost: site.boost > 0,
                          route: site.route ? {
                            distance: site.route.distance,
                            distance_str: site.route.distance_str,
                            duration_seconds: site.route.duration_seconds,
                            duration_str: site.route.duration_str
                          } : null
                        }}
                        className="hover:underline block truncate text-sm"
                      >
                        {site.site_name}
                      </Link>
                      <div className="text-xs mt-0.5 opacity-80">
                        {site.weather && site.weather.main ? (
                          <div className="flex items-center">
                            <span>{site.weather.main}</span>
                            <span className="ml-1.5">
                              {site.weather.temp}°C
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-300">No weather data</span>
                        )}
                        {site.route && (
                          <div className="text-purple-300">
                            {site.route.duration_str} ({site.route.distance_str})
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-24 text-right text-white">
                      <div className="text-sm font-medium">
                        {Number(site.final_score).toFixed(2)}
                      </div>
                      {(site.weather_score > 0 || site.boost > 0 || site.event_score > 0 || site.route_score > 0) && (
                        <div className="text-xs mt-0.5 space-y-0.5">
                          {site.weather_score > 0 && (
                            <div className="text-green-300">
                              +{Number(site.weather_score).toFixed(2)} weather
                            </div>
                          )}
                          {site.boost > 0 && (
                            <div className="text-yellow-300">
                              +{Number(site.boost).toFixed(2)} boost
                            </div>
                          )}
                          {site.event_score > 0 && (
                            <div className="text-blue-300">
                              +{Number(site.event_score).toFixed(2)} event
                            </div>
                          )}
                          {site.route_score > 0 && (
                            <div className="text-purple-300">
                              +{Number(site.route_score).toFixed(2)} route
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PatrolPlan;
